import { QrCode } from '@akpl/ui-tv';
import { useEffect, useRef } from 'react';

import { GUEST_URL, guestJoinUrl } from '../queue/useJoinQr';
import { useStore } from '../store';
import { useT } from '../useT';
import styles from './Join.module.css';

/** The QR + address + code block, shared by the join dialog and the Home card. */
export function JoinQr({ code, size = 320 }: { code: string; size?: number }) {
  const { t } = useT();
  return (
    <div className={styles.qrBlock}>
      <QrCode value={guestJoinUrl(code)} size={size} margin={1} />
      <div className={styles.qrText}>
        <div>{t('tv:join.scanToJoin')}</div>
        <div className={styles.code}>
          <span className={styles.link}>{GUEST_URL.replace('https://', '')}</span>
          <span>{' · '}</span>
          <span>
            {t('common:session.code')} <b className={styles.codeValue}>{code}</b>
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * "Únete" on Home (UX-303).
 *
 * A guest who walks into the room and looks at an idle TV had nothing to act on: the join code
 * lived two presses away behind "Fila", or on Search's side panel, and Home — the screen a room
 * leaves up — never showed it. Joining changed Home not at all, either.
 *
 * So Home carries a small, read-only card with the QR, the address and the code. It is not
 * focusable: it is a read-out, like the "SIGUIENTE" card it sits opposite, and the D-pad has
 * nothing to do with it.
 */
export function JoinCard() {
  const { t } = useT();
  const code = useStore((s) => s.session?.joinCode ?? s.meta?.joinCode ?? '');
  if (!code) return null;

  return (
    <aside className={styles.card} data-testid="home-join" aria-live="off">
      <QrCode value={guestJoinUrl(code)} size={132} margin={1} />
      <div className={styles.cardText}>
        <div className={styles.kicker}>{t('tv:join.title')}</div>
        <div className={styles.cardBody}>{t('tv:join.homeHint')}</div>
        <div className={styles.cardCode}>
          {t('common:session.code')} <b className={styles.codeValue}>{code}</b>
        </div>
      </div>
    </aside>
  );
}

/**
 * Says so when somebody joins (UX-303): a guest arriving used to change nothing on the TV — no
 * toast, no participant, no counter — so the first the room knew of her was a song landing in
 * the queue minutes later. The host's own arrival is not an event, and neither is the first
 * snapshot of the members node, which would otherwise announce everybody on every boot.
 */
export function useJoinAnnounce(): void {
  const { t } = useT();
  const members = useStore((s) => s.members);
  const hostUid = useStore((s) => s.session?.hostUid ?? s.meta?.hostUid ?? null);
  const showToast = useStore((s) => s.showToast);
  const known = useRef<Set<string> | null>(null);

  useEffect(() => {
    const uids = Object.keys(members);
    // The baseline is the first snapshot that has anybody in it: an empty one is "the listener
    // has not answered yet", and adopting it would announce the whole room on every boot.
    if (known.current === null) {
      if (uids.length > 0) known.current = new Set(uids);
      return;
    }
    for (const uid of uids) {
      if (known.current.has(uid) || uid === hostUid) continue;
      const name = members[uid]?.name;
      if (name) showToast(t('tv:join.joined', { name }));
    }
    known.current = new Set(uids);
  }, [members, hostUid, showToast, t]);
}
